/**
 * @fileoverview API Client Factory - creates configured instances of specialized API clients
 * Provides centralized configuration and interceptor management
 */

import { 
  BacktestApiClient, 
  StrategyApiClient, 
  AuthApiClient, 
  TradingApiClient 
} from './clients';
import { 
  createAuthInterceptor, 
  createLoggingInterceptor, 
  createErrorTransformInterceptor 
} from './core/interceptors';
import type { RequestInterceptor, ResponseInterceptor } from './core/types';
import type { ApiRequestConfig, ApiResponse } from './core/types';
import type { ApiClientConfig } from './core/types';

// Default configuration
const DEFAULT_CONFIG: ApiClientConfig = {
  timeout: 30000,
  retries: 3,
  retryDelay: 1000,
  headers: {
    'Content-Type': 'application/json',
  },
};

// Configuration for different environments
export const API_CONFIGS = {
  development: {
    ...DEFAULT_CONFIG,
    baseURL: '/api',
  },
  production: {
    ...DEFAULT_CONFIG,
    baseURL: import.meta.env.VITE_API_URL || '/api',
  },
  test: {
    ...DEFAULT_CONFIG,
    baseURL: 'http://localhost:5000/api',
    timeout: 5000,
  },
} as const;

/**
 * API Client Factory class
 * Creates and configures specialized API clients with shared interceptors
 */
export class ApiClientFactory {
  private config: { baseURL: string } & ApiClientConfig;
  private _backtest?: BacktestApiClient;
  private _strategy?: StrategyApiClient;
  private _auth?: AuthApiClient;
  private _trading?: TradingApiClient;

  constructor(environment: keyof typeof API_CONFIGS = 'development') {
    this.config = API_CONFIGS[environment];
  }

  /**
   * Get configured BacktestApiClient instance
   */
  get backtest(): BacktestApiClient {
    if (!this._backtest) {
      this._backtest = new BacktestApiClient(this.config);
      this.setupInterceptors(this._backtest);
    }
    return this._backtest;
  }

  /**
   * Get configured StrategyApiClient instance
   */
  get strategy(): StrategyApiClient {
    if (!this._strategy) {
      this._strategy = new StrategyApiClient(this.config);
      this.setupInterceptors(this._strategy);
    }
    return this._strategy;
  }

  /**
   * Get configured AuthApiClient instance
   */
  get auth(): AuthApiClient {
    if (!this._auth) {
      this._auth = new AuthApiClient(this.config);
      this.setupInterceptors(this._auth);
    }
    return this._auth;
  }

  /**
   * Get configured TradingApiClient instance
   */
  get trading(): TradingApiClient {
    if (!this._trading) {
      this._trading = new TradingApiClient(this.config);
      this.setupInterceptors(this._trading);
    }
    return this._trading;
  }

  /**
   * Setup common interceptors for a client
   */
  private setupInterceptors(client: { addRequestInterceptor: (interceptor: RequestInterceptor) => void; addResponseInterceptor: (interceptor: ResponseInterceptor) => void }): void {
    // Add request interceptors
    client.addRequestInterceptor(createAuthInterceptor());
    client.addRequestInterceptor(createLoggingInterceptor());

    // Add response interceptors
    client.addResponseInterceptor(createErrorTransformInterceptor());
    
    // Development-only interceptors
    if (import.meta.env.DEV) {
      client.addRequestInterceptor({
        onRequest: async (config: ApiRequestConfig) => {
          return config;
        }
      });

      client.addResponseInterceptor({
        onResponse: async (response: ApiResponse<any>) => {
          return response;
        }
      });
    }
  }

  /**
   * Update configuration for all clients
   */
  updateConfig(newConfig: Partial<ApiClientConfig>): void {
    this.config = { ...this.config, ...newConfig };
    
    // Clear cached instances to force recreation with new config
    this._backtest = undefined;
    this._strategy = undefined;
    this._auth = undefined;
    this._trading = undefined;
  }

  /**
   * Set authentication token for all clients
   */
  setAuthToken(token: string): void {
    this.updateConfig({
      headers: {
        ...this.config.headers,
        Authorization: `Bearer ${token}`,
      },
    });
  }

  /**
   * Clear authentication token from all clients
   */
  clearAuthToken(): void {
    const { Authorization, ...headers } = this.config.headers || {};
    this.updateConfig({ headers });
  }

  /**
   * Get all client instances (useful for cleanup)
   */
  getAllClients(): Array<BacktestApiClient | StrategyApiClient | AuthApiClient | TradingApiClient> {
    const clients = [];
    if (this._backtest) clients.push(this._backtest);
    if (this._strategy) clients.push(this._strategy);
    if (this._auth) clients.push(this._auth);
    if (this._trading) clients.push(this._trading);
    return clients;
  }
}

// Default factory instance
const detectedEnvironment = import.meta.env.MODE === 'production' ? 'production' : 'development';

export const apiClientFactory = new ApiClientFactory(detectedEnvironment);

// Export convenience instances
export const backtestApi = apiClientFactory.backtest;
export const strategyApi = apiClientFactory.strategy;
export const authApi = apiClientFactory.auth;
export const tradingApi = apiClientFactory.trading;
